import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatApiError,
  getTeamsPlanningPending,
  sendTeamsPlanningReminders,
} from '../../services/api';
import { getDateLocale } from '../../i18n';
import { formatLastReminder } from './constants';

function fullName(user) {
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
}

function formatDateShort(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' });
}

const STATUS_FILTER_OPTIONS = [
  { value: 'declared', labelKey: 'statusDeclared' },
  { value: 'partial', labelKey: 'statusPartial' },
  { value: 'not_declared', labelKey: 'statusNotDeclared' },
];

const DEFAULT_STATUS_FILTERS = {
  declared: true,
  partial: true,
  not_declared: true,
};

const REMIND_COOLDOWN_MS = 5000;

export default function PendingDeclarationsCard({ filters }) {
  const { t } = useTranslation(['planning', 'common']);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState(DEFAULT_STATUS_FILTERS);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [sendingIds, setSendingIds] = useState(() => new Set());
  const [cooldownUntil, setCooldownUntil] = useState({});
  const [now, setNow] = useState(() => Date.now());
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const searchInputRef = useRef(null);

  const showToast = useCallback((message, type = 'error') => {
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    const hasActiveCooldown = Object.values(cooldownUntil).some((until) => until > Date.now());
    if (!hasActiveCooldown) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  const isOnCooldown = useCallback(
    (userId) => (cooldownUntil[userId] || 0) > now,
    [cooldownUntil, now]
  );

  useEffect(() => {
    let cancelled = false;

    const loadPending = async () => {
      setLoading(true);
      setError(null);
      setActionMessage(null);
      setSendingIds(new Set());
      setCooldownUntil({});
      try {
        const data = await getTeamsPlanningPending(filters || {});
        if (cancelled) return;
        setUsers(
          (data?.results || []).map((user) => ({
            ...user,
          }))
        );
      } catch (err) {
        if (cancelled) return;
        const formatted = formatApiError(err);
        setError(formatted.generalError || t('failedToLoadPending'));
        setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPending();
    return () => {
      cancelled = true;
    };
  }, [filters?.date_from, filters?.date_to, filters?.team, filters?.member, filters?.centre, filters?.working_days]);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return users.filter((user) => {
      const status = user.declaration_status || 'not_declared';
      if (!statusFilters[status]) return false;

      const name = fullName(user).toLowerCase();
      const reversed = `${user.last_name || ''} ${user.first_name || ''}`.toLowerCase();
      const email = String(user.email || '').toLowerCase();
      const team = String(user.team || '').toLowerCase();
      const centre = String(user.centre || '').toLowerCase();
      return (
        !normalizedQuery ||
        name.includes(normalizedQuery) ||
        reversed.includes(normalizedQuery) ||
        email.includes(normalizedQuery) ||
        team.includes(normalizedQuery) ||
        centre.includes(normalizedQuery)
      );
    });
  }, [searchQuery, statusFilters, users]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilters, users, perPage]);

  const toggleStatusFilter = (value) => {
    setStatusFilters((prev) => {
      const next = { ...prev, [value]: !prev[value] };
      // Keep at least one status selected
      if (!next.declared && !next.partial && !next.not_declared) {
        return prev;
      }
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredUsers.slice(start, start + perPage);
  }, [currentPage, filteredUsers, perPage]);
  const pageItems = useMemo(() => {
    if (totalPages <= 1) return [1];
    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    return Array.from(pages)
      .filter((value) => value >= 1 && value <= totalPages)
      .sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  const remindableUsers = filteredUsers.filter(
    (user) => user.declaration_status !== 'declared' && !isOnCooldown(user.id)
  );
  const pendingRemindCount = remindableUsers.length;
  const cooldownUsers = filteredUsers.filter(
    (user) => user.declaration_status !== 'declared' && isOnCooldown(user.id)
  );
  const allOnCooldown =
    filteredUsers.some((user) => user.declaration_status !== 'declared') &&
    pendingRemindCount === 0 &&
    cooldownUsers.length > 0;

  const markReminded = (userIds, sentAtIso) => {
    const until = Date.now() + REMIND_COOLDOWN_MS;
    setCooldownUntil((prev) => {
      const next = { ...prev };
      userIds.forEach((id) => {
        next[id] = until;
      });
      return next;
    });
    setUsers((prev) =>
      prev.map((user) =>
        userIds.includes(user.id)
          ? { ...user, last_reminder: sentAtIso || new Date().toISOString() }
          : user
      )
    );
  };

  const handleRemind = async (targetUsers) => {
    const targets = targetUsers.filter(
      (user) => user.declaration_status !== 'declared' && !isOnCooldown(user.id)
    );
    if (!targets.length) return;

    const ids = targets.map((user) => user.id);
    setSendingIds((prev) => new Set([...prev, ...ids]));
    setActionMessage(null);
    try {
      const result = await sendTeamsPlanningReminders(ids, filters || {});
      const sentEmails = new Set(result?.sent || []);
      const sentUsers = targets.filter((user) => sentEmails.has(user.email));
      markReminded(sentUsers.map((user) => user.id));
      const failedCount = (result?.failed || []).length;
      if (sentUsers.length && !failedCount) {
        setActionMessage(t('reminderSentToUsers', { count: sentUsers.length }));
      } else if (sentUsers.length && failedCount) {
        setActionMessage(t('reminderPartialFail', { sent: sentUsers.length, failed: failedCount }));
        showToast(t('failedReminderToast', { count: failedCount }));
      } else {
        setActionMessage(t('noRemindersSent'));
        showToast(t('noRemindersSentRetry'));
      }
    } catch (err) {
      const formatted = formatApiError(err);
      const msg = formatted.generalError || t('failedToSendReminders');
      setActionMessage(msg);
      showToast(msg);
    } finally {
      setSendingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  return (
    <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-[#e5e7eb] shadow-sm min-w-0">
      <div className="flex flex-col gap-4 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-primary shrink-0">
                <span className="material-symbols-outlined text-[18px]">person_alert</span>
              </span>
              <h3 className="text-[#111418] text-lg font-bold truncate">{t('planningDeclarationsStatus')}</h3>
              </div>
            <p className="text-sm text-[#617289] pl-10">
              {t('declarationStatusHint')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0 pl-10 sm:pl-0">
            <div className="pending-status-filters" role="group" aria-label={t('filterByDeclarationStatus')}>
              <span className="pending-status-filters-label">{t('common:status')}</span>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`pending-status-check pending-status-check--${option.value}${statusFilters[option.value] ? ' is-active' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={statusFilters[option.value]}
                    onChange={() => toggleStatusFilter(option.value)}
                  />
                  <span className="pending-status-check-box" aria-hidden="true" />
                  <span>{t(option.labelKey)}</span>
                </label>
              ))}
            </div>
            <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-full bg-primary/10 text-primary text-sm font-bold">
              {filteredUsers.length}
            </span>
            <button
              type="button"
              className={`remind-all-btn${allOnCooldown ? ' is-sent' : ''}`}
              title={
                allOnCooldown
                  ? t('reminderSentCooldown')
                  : t('sendReminderToShown')
              }
              disabled={pendingRemindCount === 0 || sendingIds.size > 0 || loading}
              onClick={() => handleRemind(remindableUsers)}
            >
              <span className="material-symbols-outlined">
                {allOnCooldown ? 'mark_email_read' : 'outgoing_mail'}
              </span>
              <span>{allOnCooldown ? t('allSent') : t('remindAll')}</span>
            </button>
          </div>
        </div>

        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-[18px] text-[#617289]">
            search
          </span>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('searchPendingPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 rounded-lg border border-[#dbe0e6] bg-[#f9fafb] pl-10 pr-3 text-sm text-[#111418] placeholder:text-[#94a3b8] focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {actionMessage ? (
          <p className="text-xs font-medium text-primary">{actionMessage}</p>
        ) : null}
        {error ? (
          <p className="text-xs font-medium text-red-600">{error}</p>
        ) : null}
      </div>

      <div className="hidden sm:grid grid-cols-[1fr_1.1fr_0.7fr_0.7fr_auto] gap-4 px-1 py-2.5 border-b border-[#e5e7eb] text-xs font-semibold uppercase tracking-wide text-[#617289]">
        <span>{t('colName')}</span>
        <span>{t('colEmail')}</span>
        <span>{t('colStatus')}</span>
        <span>{t('colLastReminder')}</span>
        <span className="min-w-[148px] text-right">{t('colAction')}</span>
      </div>

      <div>
        <ul className="divide-y divide-[#e5e7eb]">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <li
                key={`skeleton-${i}`}
                className="px-1 py-3.5 grid grid-cols-1 sm:grid-cols-[1fr_1.1fr_0.7fr_0.7fr_auto] gap-3 sm:gap-4 items-center"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-[#f0f2f4] animate-pulse shrink-0" />
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <div className="h-3 w-32 rounded bg-[#f0f2f4] animate-pulse" />
                    <div className="h-2.5 w-24 rounded bg-[#f0f2f4] animate-pulse" />
                  </div>
                </div>
                <div className="hidden sm:block h-3 w-36 rounded bg-[#f0f2f4] animate-pulse" />
                <div className="hidden sm:block h-3 w-24 rounded bg-[#f0f2f4] animate-pulse" />
                <div className="hidden sm:block h-3 w-20 rounded bg-[#f0f2f4] animate-pulse" />
                <div className="h-9 w-32 rounded-lg bg-[#f0f2f4] animate-pulse sm:justify-self-end" />
              </li>
            ))
          ) : filteredUsers.length === 0 ? (
            <li className="px-1 py-10 text-center">
              <p className="text-sm font-semibold text-[#617289]">{t('noUsersMatchFilters')}</p>
              <p className="text-xs text-[#94a3b8] mt-1">{t('tryAdjustingFilters')}</p>
            </li>
          ) : (
            pagedUsers.map((user) => {
              const name = fullName(user);
              const lastReminderLabel = formatLastReminder(user.last_reminder);
              const isNever = !user.last_reminder;
              const initials = `${(user.first_name || '?').charAt(0)}${(user.last_name || '').charAt(0) || (user.email || '?').charAt(0)}`.toUpperCase();
              const isSending = sendingIds.has(user.id);
              const onCooldown = isOnCooldown(user.id);

              return (
                <li
                  key={user.id}
                  className={`pending-declaration-row pending-declaration-row--${user.declaration_status || 'not_declared'} px-1 py-3.5 grid grid-cols-1 sm:grid-cols-[1fr_1.1fr_0.7fr_0.7fr_auto] gap-3 sm:gap-4 items-center transition-colors`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#111418] truncate">{name}</p>
                      <p className="text-[11px] text-[#617289] truncate">
                        {(user.team || '—')} • {(user.centre || '—')}
                      </p>
                      <p className="text-xs text-[#617289] sm:hidden truncate">{user.email}</p>
                    </div>
                  </div>
                  <p className="hidden sm:block text-sm text-[#617289] truncate">{user.email}</p>
                  <div className="hidden sm:flex items-center gap-1.5">
                    {user.declaration_status === 'declared' ? (
                      <>
                        <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                        <span className="text-sm font-medium text-green-600">
                          {t('statusDeclaredRange', {
                            from: formatDateShort(user.declared_from),
                            to: formatDateShort(user.declared_to),
                          })}
                        </span>
                      </>
                    ) : user.declaration_status === 'partial' ? (
                      <>
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                        <span className="text-sm font-medium text-amber-600">
                          {t('statusPartialRange', {
                            from: formatDateShort(user.declared_from),
                            to: formatDateShort(user.declared_to),
                          })}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                        <span className="text-sm font-medium text-red-600">{t('statusNotDeclared')}</span>
                      </>
                    )}
                  </div>
                  <p
                    className={`hidden sm:block text-sm truncate ${isNever ? 'text-[#94a3b8]' : 'text-[#111418]'}`}
                    title={lastReminderLabel}
                  >
                    {lastReminderLabel}
                  </p>
                  <div className="sm:min-w-[148px] flex sm:justify-end">
                    {user.declaration_status === 'declared' ? (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <span className="material-symbols-outlined text-[16px]">check_circle</span>
                        {t('complete')}
                      </span>
                    ) : onCooldown ? (
                      <button
                        type="button"
                        className="relance-btn is-sent w-full sm:w-auto"
                        disabled
                        title={t('reminderSent')}
                      >
                        <span className="material-symbols-outlined">mark_email_read</span>
                        <span>{t('sent')}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="relance-btn w-full sm:w-auto"
                        title={t('sendReminderEmailTo', { email: user.email })}
                        disabled={isSending}
                        onClick={() => handleRemind([user])}
                      >
                        <span className="material-symbols-outlined">mail</span>
                        <span>{isSending ? t('sending') : t('reminderEmail')}</span>
                      </button>
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </div>

      {toast && (
        <div className={`pending-toast pending-toast--${toast.type}`}>
          <span className="material-symbols-outlined text-[18px]">
            {toast.type === 'error' ? 'error' : 'info'}
          </span>
          <span className="pending-toast-msg">{toast.message}</span>
          <button
            type="button"
            className="pending-toast-close"
            onClick={() => setToast(null)}
            aria-label={t('dismiss')}
          >
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>
      )}

      {!loading && filteredUsers.length > 0 ? (
        <div className="mt-4 flex flex-col gap-3 border-t border-[#e5e7eb] pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 text-sm text-[#617289]">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide">{t('common:perPage')}</span>
              <div className="pending-page-size-group" role="group" aria-label={t('pendingPerPage')}>
                {[10, 20, 30].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`pending-page-size-btn${perPage === value ? ' is-active' : ''}`}
                    onClick={() => setPerPage(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
            <span className="text-xs text-[#94a3b8]">
              {filteredUsers.length === 0
                ? t('usersCount', { count: 0 })
                : t('common:rangeOfTotal', {
                    from: (currentPage - 1) * perPage + 1,
                    to: Math.min(currentPage * perPage, filteredUsers.length),
                    total: filteredUsers.length,
                  })}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <button
              type="button"
              className="modal-dismiss-btn"
              disabled={currentPage <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              <span className="material-symbols-outlined">chevron_left</span>
              <span>{t('common:previous')}</span>
            </button>
            <div className="pending-pagination-pages" role="group" aria-label={t('pendingPages')}>
              {pageItems.map((pageNumber, index) => {
                const prev = pageItems[index - 1];
                const showGap = index > 0 && pageNumber - prev > 1;
                return (
                  <React.Fragment key={pageNumber}>
                    {showGap ? (
                      <span className="pending-pagination-gap" aria-hidden="true">
                        ...
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className={`pending-pagination-page-btn${currentPage === pageNumber ? ' is-active' : ''}`}
                      aria-current={currentPage === pageNumber ? 'page' : undefined}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            <button
              type="button"
              className="modal-dismiss-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              <span>{t('common:next')}</span>
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
